/**
 * Byte-level format signatures.
 *
 * §30 requires extension, MIME-type, file-signature, magic-byte and
 * internal-structure verification. This module owns the byte evidence; the
 * reconciliation of byte evidence against the *claimed* extension and MIME type
 * happens in `detect.ts`.
 *
 * A signature never trusts the filename. That is the whole point: §35 lists
 * "Incorrect extension" as a mandatory test case, and messaging apps routinely
 * deliver files as `application/octet-stream` regardless of content (§9).
 */

/** Byte pattern anchored at a fixed offset. `null` in `bytes` = wildcard. */
export interface Pattern {
  readonly offset: number;
  readonly bytes: readonly (number | null)[];
}

export interface Signature {
  /** Format id from the catalog, or a container id resolved later. */
  readonly format: string;
  /** All patterns must match. */
  readonly patterns: readonly Pattern[];
  /**
   * Higher wins when several signatures match. Container formats score low so
   * that a more specific signature (e.g. `webp` over `riff`) takes precedence.
   */
  readonly weight: number;
  /**
   * Set when the match only identifies a container and a second pass is needed
   * to name the real format (ZIP → DOCX/XLSX/PPTX/HWPX/EPUB/ODF/…).
   */
  readonly container?: 'zip' | 'ole2' | 'riff' | 'ebml' | 'isobmff' | 'tiff';
}

/** ASCII string → byte pattern. */
const s = (offset: number, text: string): Pattern => ({
  offset, bytes: [...text].map((c) => c.charCodeAt(0)),
});

/** Raw bytes → pattern. */
const b = (offset: number, ...bytes: (number | null)[]): Pattern => ({ offset, bytes });

/**
 * Signature table. Order is irrelevant — `weight` decides ties.
 *
 * Weights: 100 = unambiguous full identification, 60 = identifies a family that
 * still needs a brand/doctype check, 20 = generic container.
 */
export const SIGNATURES: readonly Signature[] = [
  /* ---- documents ---- */
  // The PDF spec permits leading bytes before %PDF-, so detect.ts also does a
  // bounded scan of the first 1 KiB. This anchored pattern is the fast path.
  { format: 'pdf', patterns: [s(0, '%PDF-')], weight: 100 },
  { format: 'rtf', patterns: [s(0, '{\\rtf')], weight: 100 },
  { format: 'eps', patterns: [s(0, '%!PS')], weight: 90 },
  // EPS with a binary DOS preview header wraps the PostScript.
  { format: 'eps', patterns: [b(0, 0xc5, 0xd0, 0xd3, 0xc6)], weight: 90 },

  /* ---- containers ---- */
  { format: 'zip', patterns: [b(0, 0x50, 0x4b, 0x03, 0x04)], weight: 20, container: 'zip' },
  { format: 'zip', patterns: [b(0, 0x50, 0x4b, 0x05, 0x06)], weight: 20, container: 'zip' }, // empty archive
  { format: 'zip', patterns: [b(0, 0x50, 0x4b, 0x07, 0x08)], weight: 20, container: 'zip' }, // spanned
  // OLE2 / Compound File Binary — DOC, XLS, PPT, HWP 5.0 and others.
  { format: 'ole2', patterns: [b(0, 0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1)], weight: 20, container: 'ole2' },

  /* ---- images ---- */
  { format: 'png', patterns: [b(0, 0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)], weight: 100 },
  { format: 'jpeg', patterns: [b(0, 0xff, 0xd8, 0xff)], weight: 100 },
  { format: 'gif', patterns: [s(0, 'GIF87a')], weight: 100 },
  { format: 'gif', patterns: [s(0, 'GIF89a')], weight: 100 },
  { format: 'bmp', patterns: [b(0, 0x42, 0x4d)], weight: 60 },
  { format: 'psd', patterns: [s(0, '8BPS')], weight: 100 },
  { format: 'ico', patterns: [b(0, 0x00, 0x00, 0x01, 0x00)], weight: 90 },
  { format: 'ico', patterns: [b(0, 0x00, 0x00, 0x02, 0x00)], weight: 90 }, // .cur
  { format: 'tiff', patterns: [b(0, 0x49, 0x49, 0x2a, 0x00)], weight: 60, container: 'tiff' }, // little endian
  { format: 'tiff', patterns: [b(0, 0x4d, 0x4d, 0x00, 0x2a)], weight: 60, container: 'tiff' }, // big endian
  { format: 'raw', patterns: [b(0, 0x49, 0x49, 0x2a, 0x00, 0x10, 0x00, 0x00, 0x00), s(8, 'CR')], weight: 100 }, // CR2
  { format: 'raw', patterns: [b(0, 0x46, 0x4f, 0x56, 0x62)], weight: 100 }, // X3F
  { format: 'raw', patterns: [s(0, 'FUJIFILMCCD-RAW')], weight: 100 }, // RAF

  /* ---- RIFF family: needs the form type at offset 8 ---- */
  { format: 'webp', patterns: [s(0, 'RIFF'), s(8, 'WEBP')], weight: 100 },
  { format: 'wav', patterns: [s(0, 'RIFF'), s(8, 'WAVE')], weight: 100 },
  { format: 'avi', patterns: [s(0, 'RIFF'), s(8, 'AVI ')], weight: 100 },
  { format: 'riff', patterns: [s(0, 'RIFF')], weight: 20, container: 'riff' },

  /* ---- ISO base media (ftyp at offset 4), brand at offset 8 ---- */
  { format: 'heic', patterns: [s(4, 'ftyp'), s(8, 'heic')], weight: 100 },
  { format: 'heic', patterns: [s(4, 'ftyp'), s(8, 'heix')], weight: 100 },
  { format: 'heic', patterns: [s(4, 'ftyp'), s(8, 'hevc')], weight: 100 },
  { format: 'heic', patterns: [s(4, 'ftyp'), s(8, 'mif1')], weight: 90 },
  { format: 'heic', patterns: [s(4, 'ftyp'), s(8, 'msf1')], weight: 90 },
  { format: 'avif', patterns: [s(4, 'ftyp'), s(8, 'avif')], weight: 100 },
  { format: 'avif', patterns: [s(4, 'ftyp'), s(8, 'avis')], weight: 100 },
  { format: 'mp4', patterns: [s(4, 'ftyp'), s(8, 'isom')], weight: 100 },
  { format: 'mp4', patterns: [s(4, 'ftyp'), s(8, 'mp41')], weight: 100 },
  { format: 'mp4', patterns: [s(4, 'ftyp'), s(8, 'mp42')], weight: 100 },
  { format: 'mp4', patterns: [s(4, 'ftyp'), s(8, 'M4V ')], weight: 100 },
  { format: 'mp4', patterns: [s(4, 'ftyp'), s(8, 'dash')], weight: 90 },
  { format: 'm4a', patterns: [s(4, 'ftyp'), s(8, 'M4A ')], weight: 100 },
  { format: 'mov', patterns: [s(4, 'ftyp'), s(8, 'qt  ')], weight: 100 },
  { format: 'isobmff', patterns: [s(4, 'ftyp')], weight: 20, container: 'isobmff' },

  /* ---- other media ---- */
  { format: 'mkv', patterns: [b(0, 0x1a, 0x45, 0xdf, 0xa3)], weight: 40, container: 'ebml' },
  { format: 'wmv', patterns: [b(0, 0x30, 0x26, 0xb2, 0x75, 0x8e, 0x66, 0xcf, 0x11)], weight: 90 },
  { format: 'mpeg', patterns: [b(0, 0x00, 0x00, 0x01, 0xba)], weight: 90 }, // program stream
  { format: 'mpeg', patterns: [b(0, 0x00, 0x00, 0x01, 0xb3)], weight: 90 }, // video sequence
  // MPEG transport stream: 0x47 sync byte every 188 bytes. Requiring three
  // consecutive syncs makes a false positive vanishingly unlikely, which is
  // what lets ".mts" stay registered to TypeScript without losing AVCHD video —
  // camcorder files are identified by these bytes, not by their extension.
  { format: 'mpeg', patterns: [b(0, 0x47), b(188, 0x47), b(376, 0x47)], weight: 90 },
  // M2TS/AVCHD prefixes each packet with a 4-byte arrival timestamp.
  { format: 'mpeg', patterns: [b(4, 0x47), b(196, 0x47), b(388, 0x47)], weight: 90 },
  { format: 'mp3', patterns: [s(0, 'ID3')], weight: 90 },
  { format: 'mp3', patterns: [b(0, 0xff, 0xfb)], weight: 60 },
  { format: 'mp3', patterns: [b(0, 0xff, 0xf3)], weight: 60 },
  { format: 'mp3', patterns: [b(0, 0xff, 0xf2)], weight: 60 },
  { format: 'flac', patterns: [s(0, 'fLaC')], weight: 100 },
  { format: 'ogg', patterns: [s(0, 'OggS')], weight: 60 }, // Opus also uses OggS

  /* ---- archives ---- */
  { format: 'rar', patterns: [b(0, 0x52, 0x61, 0x72, 0x21, 0x1a, 0x07, 0x00)], weight: 100 },
  { format: 'rar', patterns: [b(0, 0x52, 0x61, 0x72, 0x21, 0x1a, 0x07, 0x01, 0x00)], weight: 100 },
  { format: '7z', patterns: [b(0, 0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c)], weight: 100 },
  { format: 'gzip', patterns: [b(0, 0x1f, 0x8b)], weight: 90 },
  { format: 'bzip2', patterns: [s(0, 'BZh')], weight: 90 },
  { format: 'xz', patterns: [b(0, 0xfd, 0x37, 0x7a, 0x58, 0x5a, 0x00)], weight: 100 },
  { format: 'tar', patterns: [s(257, 'ustar')], weight: 100 },

  /* ---- data ---- */
  { format: 'sqlite', patterns: [s(0, 'SQLite format 3\0')], weight: 100 },

  /* ---- 3D / CAD ---- */
  { format: 'gltf', patterns: [s(0, 'glTF')], weight: 100 },
  { format: 'fbx', patterns: [s(0, 'Kaydara FBX Binary')], weight: 100 },
  { format: 'dwg', patterns: [s(0, 'AC10')], weight: 90 },
  { format: 'dwg', patterns: [s(0, 'AC1.')], weight: 60 },
];

/** Longest offset+length we ever inspect. Read at least this much for detection. */
export const MAX_SIGNATURE_REACH = 512;

function matchPattern(head: Uint8Array, p: Pattern): boolean {
  if (p.offset + p.bytes.length > head.length) return false;
  for (let i = 0; i < p.bytes.length; i++) {
    const expected = p.bytes[i];
    if (expected === null || expected === undefined) continue;
    if (head[p.offset + i] !== expected) return false;
  }
  return true;
}

export interface SignatureMatch {
  readonly format: string;
  readonly weight: number;
  readonly container: Signature['container'] | undefined;
}

/**
 * All signatures matching `head`, strongest first.
 *
 * `head` should be at least {@link MAX_SIGNATURE_REACH} bytes when available;
 * shorter buffers simply cause deep patterns (e.g. tar at 257) to miss rather
 * than to throw, which keeps truncated-download handling (§35) well defined.
 */
export function matchSignatures(head: Uint8Array): SignatureMatch[] {
  const out: SignatureMatch[] = [];
  for (const sig of SIGNATURES) {
    if (sig.patterns.every((p) => matchPattern(head, p))) {
      out.push({ format: sig.format, weight: sig.weight, container: sig.container });
    }
  }
  return out.sort((a, x) => x.weight - a.weight);
}

/**
 * The PDF specification tolerates bytes before `%PDF-`, and real-world files
 * produced by broken generators rely on it. Scan a bounded prefix so a valid
 * PDF is not rejected, without turning detection into an unbounded search.
 */
export function findPdfHeaderOffset(head: Uint8Array, limit = 1024): number {
  const needle = [0x25, 0x50, 0x44, 0x46, 0x2d]; // %PDF-
  const end = Math.min(head.length - needle.length, limit);
  outer: for (let i = 0; i <= end; i++) {
    for (let j = 0; j < needle.length; j++) if (head[i + j] !== needle[j]) continue outer;
    return i;
  }
  return -1;
}
